import { useEffect, useRef } from "react";
import { BaseFeeTiming, BillingPricingMode } from "@api/shared-runtime/enums";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BASE_FEE_TIMING_LABELS,
  formatPlanCurrency,
  formatPlanDate,
  FREE_CATEGORY_LABEL,
  PRICING_UNAVAILABLE_LABEL,
  getSelectedCategoryRate,
  isFreeCategoryRate,
  type BillingPlanCategory,
  type BillingPlanPreview as Preview,
} from "./billing-plan-values";
import {
  SAILE_CHARGES_LABEL,
  WAGE_PASS_THROUGH_LABEL,
  WAGE_PASS_THROUGH_PLAN_LABEL,
  WAGE_PASS_THROUGH_PLAN_OFF,
  WAGE_PASS_THROUGH_PLAN_ON,
} from "./wage-pass-through-copy";

export function BillingPlanPreview({
  preview,
  categories,
}: {
  preview: Preview;
  categories: BillingPlanCategory[];
}) {
  const hasPricedCategories = preview.summary.categories.some(
    (category) => category.pricingMode !== null,
  );
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [preview]);
  return (
    <section
      ref={sectionRef}
      className="space-y-3 rounded-lg border bg-blue-50/40 p-4"
      aria-label="Plan preview"
    >
      <h3 className="font-semibold">Review plan</h3>
      <dl className="grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-muted-foreground">Start date (New York)</dt>
          <dd>{formatPlanDate(preview.startDate)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">First monthly cycle</dt>
          <dd>
            {formatPlanDate(preview.startDate)} –{" "}
            {formatPlanDate(preview.periodLastDate)} (inclusive)
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Base fee billing</dt>
          <dd>{BASE_FEE_TIMING_LABELS[preview.baseFeeTiming]}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            {WAGE_PASS_THROUGH_PLAN_LABEL}
          </dt>
          <dd>
            {preview.wagePassThroughEnabled
              ? WAGE_PASS_THROUGH_PLAN_ON
              : WAGE_PASS_THROUGH_PLAN_OFF}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Full cycle base fee</dt>
          <dd>
            {formatPlanCurrency(preview.summary.baseMonthlyAmount)} — applies in
            full to every started cycle, never prorated
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-muted-foreground">Invoice schedule</dt>
          <dd>
            {!preview.invoicingEnabled && (
              <p>
                Not invoiced: usage is reported only. Turn invoicing on from the
                plan card when ready.
              </p>
            )}
            <ul className={preview.invoicingEnabled ? "space-y-0.5" : "hidden"}>
              <li>{describeScheduledInvoice(preview.schedule.firstInvoice)}</li>
              <li>{describeScheduledInvoice(preview.schedule.nextInvoice)}</li>
              {preview.baseFeeTiming === BaseFeeTiming.InAdvance &&
                (hasPricedCategories ? (
                  <li>
                    After the plan ends: usage from the last cycle, only if
                    there is any.
                  </li>
                ) : (
                  <li>No usage lines: this plan has no priced categories.</li>
                ))}
              <li className="text-muted-foreground">
                Once invoicing is enabled.
              </li>
            </ul>
          </dd>
        </div>
      </dl>
      {preview.isIllustration ? (
        <p className="text-sm">
          Future usage charges are unknown. The amounts below illustrate these
          rates using calendar-month usage from {preview.usageMonth}. Earlier
          usage is not carried into this future plan.
        </p>
      ) : (
        <div className="space-y-1 text-sm">
          <p>
            Recorded usage in this service period includes{" "}
            {preview.recordedJobPostCount} recorded job posts (including any
            historical backfill).
          </p>
          <p>
            Older reveals, signatures, and shifts do not have a general
            historical backfill. This preview covers recorded events only.
          </p>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Category</TableHead>
            <TableHead>Usage / included</TableHead>
            <TableHead>Rate</TableHead>
            <TableHead className="text-right">Charge</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {preview.summary.categories.map((category) => {
            const supportsPricing = categories.some(
              (item) =>
                item.categoryCode === category.categoryCode && item.supported,
            );
            let charge = formatPlanCurrency(category.lineAmount ?? "0");
            if (isFreeCategoryRate(getSelectedCategoryRate(category))) {
              charge = FREE_CATEGORY_LABEL;
            }
            if (category.pricingMode === null && !supportsPricing) {
              charge = PRICING_UNAVAILABLE_LABEL;
            }
            return (
              <TableRow key={category.categoryCode}>
                <TableCell>{category.displayName}</TableCell>
                <TableCell>
                  {Number(category.usedQuantity)} /{" "}
                  {category.includedQuantity ?? "—"}
                </TableCell>
                <TableCell>
                  {getPreviewRateLabel({ category, supportsPricing })}
                </TableCell>
                <TableCell className="text-right">{charge}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={3} className="text-muted-foreground">
              {SAILE_CHARGES_LABEL}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatPlanCurrency(preview.summary.saileChargesAmount)}
            </TableCell>
          </TableRow>
          {preview.summary.wagePassThroughAmount !== null && (
            <TableRow>
              <TableCell colSpan={3}>{WAGE_PASS_THROUGH_LABEL}</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatPlanCurrency(preview.summary.wagePassThroughAmount)}
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell colSpan={3} className="font-semibold">
              {preview.isIllustration
                ? "Illustrative total"
                : "Current recorded-usage estimate"}
            </TableCell>
            <TableCell className="text-right font-semibold tabular-nums">
              {formatPlanCurrency(preview.summary.totalAmount)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
      {preview.missingBasisCount > 0 && (
        <p role="alert" className="text-sm text-amber-800">
          {preview.missingBasisCount} usage events are missing a wage basis. The
          amount shown excludes those charges; they must be resolved before
          invoicing.
        </p>
      )}
    </section>
  );
}

function describeScheduledInvoice(
  invoice: Preview["schedule"]["firstInvoice"],
): string {
  const baseFee = `base fee for ${formatPlanDate(invoice.baseFeePeriod.start)} – ${formatPlanDate(invoice.baseFeePeriod.lastDate)}`;
  if (!invoice.usagePeriod) {
    return `${formatPlanDate(invoice.date)}: ${baseFee}`;
  }
  if (invoice.usagePeriod.start === invoice.baseFeePeriod.start) {
    return `${formatPlanDate(invoice.date)}: ${baseFee} and its usage`;
  }
  return `${formatPlanDate(invoice.date)}: ${baseFee} + usage from ${formatPlanDate(invoice.usagePeriod.start)} – ${formatPlanDate(invoice.usagePeriod.lastDate)}`;
}

function getPreviewRateLabel({
  category,
  supportsPricing,
}: {
  category: Preview["summary"]["categories"][number];
  supportsPricing: boolean;
}): string {
  if (category.pricingMode === null && !supportsPricing) {
    return PRICING_UNAVAILABLE_LABEL;
  }
  if (isFreeCategoryRate(getSelectedCategoryRate(category))) {
    return FREE_CATEGORY_LABEL;
  }
  if (category.pricingMode === BillingPricingMode.PercentOfBasis) {
    return `${Number(category.basisPercent)}% of wages`;
  }
  return formatPlanCurrency(category.unitAmount ?? "0");
}
